import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, colors } from "@material-ui/core";
import { Link } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import { ButtonLink } from "../../../components/button-link/ButtonLink";
import { useAuthenticationContext } from "../../../context/UserContext";
import { BusinessSearchScreen } from "../../business-search/BusinessSearchScreen";
import { BranchManagerDashboardScreen } from "../../branch_manager_dashboard_screen/BranchManagerDashboardScreen";
import BranchManager from "../../../models/BranchManager";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
  },

  items: {},

  itemsWrapper: {
    position: "relative",
    width: 220,
    height: 200,

    borderRadius: 4,
    border: "1px solid #e1e4e8",
    marginRight: 20,
    marginBottom: 20,

    "& .overlay": {
      position: "absolute",
      top: 0,
      left: 0,

      height: "100%",
      width: "100%",
      backgroundColor: "transparent",
    },
  },

  item: {
    position: "relative",
    display: "block",

    width: "100%",
    height: "100%",
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,

    "&:hover": {
      cursor: "pointer",
      backgroundColor: "#fcfcfb",
      color: theme.palette.text.primary,
    },

    "& .avatarWrapper": {
      padding: "24px 16px 0 16px",

      "& img": {
        width: 30,
        height: 30,
        borderRadius: 30,
        marginBottom: 4,
      },

      "& .avatarContent": {
        display: "inline-block",
        width: "100%",
      },

      "& span": {
        display: "block",
      },

      "& .title": {
        fontSize: 14,
        fontWeight: theme.typography.fontWeightMedium,
      },

      "& .subTitle": {
        fontSize: 12,
        fontWeight: theme.typography.fontWeightLight,
        color: colors.grey[600],

        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
      },
    },

    "& .typeWrapper": {
      display: "inline-block",
      padding: "6px 16px 6px 16px",
      margin: "18px 0",
      border: "1px solid #e1e4e8",
      fontSize: 12,
      fontWeight: theme.typography.fontWeightMedium,

      background: theme.palette.common.white,
      borderRadius: "0 4px 4px 0",
      borderLeft: "none",

      "&.personal": {
        color: colors.orange[800],
      },

      "&.enterprise": {
        color: theme.palette.primary.main,
      },
    },

    "& .statusWrapper": {
      position: "absolute",
      bottom: 14,
      left: 0,
      padding: "0 18px",

      "& span": {
        display: "inline-block",
        fontSize: 12,
        fontWeight: theme.typography.fontWeightLight,
      },

      "& .globalStatus": {
        height: 10,
        width: 10,
        borderRadius: 20,
        marginRight: 12,

        "&.enabled": {
          backgroundColor: colors.green[800],
        },

        "&.waiting": {
          backgroundColor: theme.palette.error.main,
        },
      },
    },
  },

  emptyWrapper: {
    height: 180,
    width: "100%",
    fontSize: 14,

    "& div": {
      textAlign: "center",
    },
  },
}));

export const BranchItem = (props: any) => {
  const classes = useStyles(props);
  const intl = useIntl();
  // Context
  const [user] = useAuthenticationContext();

  const emptyWrapper = () => {
    return (
      <Box
        className={classes.emptyWrapper}
        display="flex"
        flexWrap="nowrap"
        alignItems="center"
        justifyContent="center"
      >
        <Box>
          <Box component="h4" mb={3} mt={2}>
            <FormattedMessage id="sharedScreen.noBranches" />
          </Box>
          <ButtonLink
            to={BusinessSearchScreen.routeName}
            text={intl.formatMessage({ id: "sharedScreen.manageBranch" })}
          />
        </Box>
      </Box>
    );
  };

  const renderBranchManagerStatusClass = (status: boolean) => {
    return status
      ? [<FormattedMessage id="sharedScreen.activated" />, "enabled"]
      : [<FormattedMessage id="sharedScreen.waitingValidation" />, "waiting"];
  };

  const renderItems = (manager: BranchManager) => {
    const [statusMessage, statusClassName] = renderBranchManagerStatusClass(
      manager.isApproved()
    );

    return (
      <Box component="div" className={classes.itemsWrapper}>
        <Link
          to={`${BranchManagerDashboardScreen.routeName}?branch=${manager.business_branch.id}`}
          className={classes.item}
        >
          <Box className="avatarWrapper">
            <div>
              <img src={manager.business_branch.avatar()} alt="" />
            </div>
            <div className="avatarContent">
              <span className="title">
                {manager.business_branch.fullName()}
              </span>
              <span className="subTitle">
                {manager.business_branch.fullAddress()}
              </span>
            </div>
          </Box>
          <Box
            className="statusWrapper"
            display="flex"
            flexWrap="nowrap"
            alignItems="center"
          >
            <span className={`globalStatus ${statusClassName}`} />
            <span>{statusMessage}</span>
          </Box>
        </Link>

        {manager.isApproved() ? (
          <></>
        ) : (
          <Box component="div" className="overlay" />
        )}
      </Box>
    );
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.items} display="flex" flexWrap="wrap">
        {user.branch_managers.length === 0
          ? emptyWrapper()
          : user.branch_managers.map((manager) => renderItems(manager))}
      </Box>
    </Box>
  );
};
